import { takeLatest, put, call, all, fork } from 'redux-saga/effects';
import WebCheckInTypes from './webCheckIn.types';
import {
	createWebCheckIn,
	getTodayAttendanceStatus,
	regularizeAttendance,
} from './webCheckIn.services';
import {
	createWebCheckInSuccess,
	createWebCheckInFailure,
	fetchTodayAttendanceSuccess,
	fetchTodayAttendanceFailure,
	regularizeAttendanceSuccess,
	regularizeAttendanceFailure,
} from './webCheckIn.action';
import AlertManager from '../../utils/alertManager';
import { encryptData } from '../_common/enode-decode';

const buildEncryptedCreateFormData = (formData: FormData) => {
	const payloadForEncryption: Record<string, any> = {};
	formData.forEach((value, key) => {
		if (key !== 'picture') {
			payloadForEncryption[key] = value;
		}
	});
	const encryptedPayload = encryptData(
		payloadForEncryption,
		WebCheckInTypes.CREATE_WEB_CHECK_IN_KEY,
	);
	const requestFormData = new FormData();
	requestFormData.append('CreateEmployeeTemporaryAttendanceToken', encryptedPayload);
	const pictureValue = formData.get('picture');
	if (pictureValue) {
		if (typeof File !== 'undefined' && pictureValue instanceof File) {
			requestFormData.append('picture', pictureValue);
		} else {
			requestFormData.append('picture', pictureValue as Blob);
		}
	}
	return requestFormData;
};

const buildEncryptedCreatePayloadFromObject = (data: Record<string, any>) => {
	const encryptedPayload = encryptData(data, WebCheckInTypes.CREATE_WEB_CHECK_IN_KEY);
	const requestFormData = new FormData();
	requestFormData.append('CreateEmployeeTemporaryAttendanceToken', encryptedPayload);
	return requestFormData;
};

// Create Web Check-In Saga
export function* createWebCheckInSaga(payload: any) {
	try {
		const requestPayload =
			payload?.payload instanceof FormData
				? buildEncryptedCreateFormData(payload.payload)
				: buildEncryptedCreatePayloadFromObject(payload?.payload || {});
		// @ts-ignore
		const response = yield call(createWebCheckIn as any, requestPayload);
		const responseData = response?.data;
		if (responseData?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}
		if (responseData?.status === 'success') {
			yield put(createWebCheckInSuccess(responseData));
		} else {
			yield put(createWebCheckInFailure(responseData));
		}
	} catch (error: any) {
		yield put(createWebCheckInFailure(error?.response?.data?.message || 'An error occurred'));
	}
}

export function* fetchTodayAttendanceSaga(payload: any) {
	try {
		const encryptedPayload = encryptData(
			payload.payload,
			WebCheckInTypes.FETCH_TODAY_ATTENDANCE_KEY,
		);
		// @ts-ignore
		const response = yield call(getTodayAttendanceStatus as any, encryptedPayload);
		const responseData = response?.data;
		if (responseData?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}
		if (responseData?.status === 'success') {
			yield put(fetchTodayAttendanceSuccess(responseData));
		} else {
			yield put(fetchTodayAttendanceFailure(responseData));
		}
	} catch (error: any) {
		yield put(
			fetchTodayAttendanceFailure(
				error?.response?.data?.message || 'Unable to fetch attendance',
			),
		);
	}
}

export function* regularizeAttendanceSaga(payload: any) {
	try {
		const encryptedPayload = encryptData(
			payload.payload,
			WebCheckInTypes.REGULARIZE_ATTENDANCE_KEY,
		);
		// @ts-ignore
		const response = yield call(regularizeAttendance as any, encryptedPayload);
		const responseData = response?.data;
		if (responseData?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}
		if (responseData?.status === 'success') {
			yield put(regularizeAttendanceSuccess(responseData));
		} else {
			yield put(regularizeAttendanceFailure(responseData));
		}
	} catch (error: any) {
		yield put(
			regularizeAttendanceFailure(
				error?.response?.data?.message || 'Unable to regularise attendance',
			),
		);
	}
}

// Watcher Sagas
export function* onCreateWebCheckInStart(): Generator<any, void, any> {
	yield takeLatest(WebCheckInTypes.CREATE_WEB_CHECK_IN_START as any, createWebCheckInSaga);
}

export function* onFetchTodayAttendanceStart(): Generator<any, void, any> {
	yield takeLatest(WebCheckInTypes.FETCH_TODAY_ATTENDANCE_START as any, fetchTodayAttendanceSaga);
}

export function* onRegularizeAttendanceStart(): Generator<any, void, any> {
	yield takeLatest(WebCheckInTypes.REGULARIZE_ATTENDANCE_START as any, regularizeAttendanceSaga);
}

// Main Saga
export function* webCheckInSagaDetails(): Generator<any, void, any> {
	yield all([
		fork(onCreateWebCheckInStart),
		fork(onFetchTodayAttendanceStart),
		fork(onRegularizeAttendanceStart),
	]);
}
